export type Outcome = 'success' | 'error' | 'timeout' | 'cancelled' | 'abandoned'
type TraceName =
  | 'edge_api'
  | 'edge_screen'
  | 'edge_upload'
  | 'edge_form'
  | 'edge_ocr'
  | 'edge_web_health'
type Metrics = Record<string, number>
type Bridge = { postMessage: (message: string) => void }
type ScreenLoad = {
  name: string
  finish: ReturnType<typeof startTrace>
  ready: boolean
  pending: number
  frame?: number
}
let sequence = 0
let screen: ScreenLoad | undefined
let monitoringCleanup: (() => void) | undefined

const bridge = () => (window as Window & { EdgePerformance?: Bridge }).EdgePerformance
export const isPerformanceMonitoringEnabled = () => typeof bridge()?.postMessage === 'function'
const send = (target: Bridge | undefined, message: object) => {
  try {
    target?.postMessage(JSON.stringify(message))
  } catch {
    // Monitoring failures must not block the application.
  }
}

export const failureOutcome = (error: unknown): Outcome => {
  const details =
    error && typeof error === 'object' ? (error as { name?: string; code?: string }) : undefined
  if (details?.name === 'AbortError' || details?.code === 'ERR_CANCELED') return 'cancelled'
  if (['ECONNABORTED', 'ETIMEDOUT'].includes(details?.code)) return 'timeout'
  return 'error'
}

export function startTrace(name: TraceName, attributes: Record<string, string> = {}) {
  const target = bridge()
  if (typeof target?.postMessage !== 'function') return () => undefined
  const id = `${Date.now().toString(36)}_${++sequence}`
  const started = performance.now()
  let finished = false
  const currentScreen = name === 'edge_api' ? screen : undefined
  if (currentScreen) currentScreen.pending++
  // Attribute values are static operation/screen names, never form values or URLs.
  send(target, {
    type: 'start',
    id,
    name,
    attributes: {
      ...attributes,
      web_release: process.env.REACT_APP_WEB_RELEASE || 'unknown',
    },
  })
  return (outcome: Outcome = 'success', metrics: Metrics = {}) => {
    if (finished) return
    finished = true
    send(target, {
      type: 'end',
      id,
      outcome,
      metrics: { ...metrics, duration_ms: Math.round(performance.now() - started) },
    })
    if (currentScreen) {
      currentScreen.pending--
      if (currentScreen === screen) scheduleUsable()
    }
  }
}

const scheduleUsable = () => {
  const current = screen
  if (!current?.ready || current.pending || current.frame || document.visibilityState === 'hidden')
    return
  // Wait for a paint after React commits the data and controls.
  current.frame = requestAnimationFrame(() => {
    current.frame = requestAnimationFrame(() => {
      current.frame = undefined
      if (current !== screen || current.pending || !current.ready) return
      current.finish('success')
      send(bridge(), { type: 'usable' })
      screen = undefined
    })
  })
}

export const beginScreen = (name: string) => {
  if (screen?.frame) cancelAnimationFrame(screen.frame)
  screen?.finish('abandoned')
  if (!bridge()) {
    screen = undefined
    return () => undefined
  }
  const current: ScreenLoad = {
    name,
    finish: startTrace('edge_screen', { screen: name }),
    ready: false,
    pending: 0,
  }
  screen = current
  return () => {
    if (screen !== current) return
    if (screen.frame) cancelAnimationFrame(screen.frame)
    screen.finish('abandoned')
    screen = undefined
  }
}

export const screenLoading = (name: string) => {
  if (screen?.name !== name) return
  screen.ready = false
  if (screen.frame) cancelAnimationFrame(screen.frame)
  screen.frame = undefined
}

export const screenReady = (name: string, failed = false) => {
  if (screen?.name !== name) return
  if (failed) {
    if (screen.frame) cancelAnimationFrame(screen.frame)
    screen.finish('error')
    screen = undefined
    return
  }
  screen.ready = true
  scheduleUsable()
}

export const reportWebError = (kind: 'js_error' | 'unhandled_rejection') =>
  send(bridge(), { type: 'error', kind })

export const initializePerformance = () => {
  if (monitoringCleanup) return monitoringCleanup
  if (!bridge()) return () => undefined
  const onError = () => reportWebError('js_error')
  const onRejection = () => reportWebError('unhandled_rejection')
  window.addEventListener('error', onError)
  window.addEventListener('unhandledrejection', onRejection)
  let health: ReturnType<typeof startTrace> | undefined
  let healthTimer: ReturnType<typeof setInterval> | undefined
  let longTasks = 0
  let blockedMs = 0
  let observer: PerformanceObserver | undefined
  const record = (entries: PerformanceEntry[]) => {
    for (const entry of entries) {
      longTasks++
      blockedMs += entry.duration
    }
  }
  if (
    typeof PerformanceObserver !== 'undefined' &&
    PerformanceObserver.supportedEntryTypes?.includes('longtask')
  ) {
    observer = new PerformanceObserver((list) => record(list.getEntries()))
  }
  const stopHealth = () => {
    clearInterval(healthTimer)
    record(observer?.takeRecords() || [])
    observer?.disconnect()
    health?.('success', { long_tasks: longTasks, blocked_ms: Math.round(blockedMs) })
    health = undefined
    longTasks = 0
    blockedMs = 0
  }
  const startHealth = () => {
    if (health || !observer || document.visibilityState === 'hidden') return
    health = startTrace('edge_web_health')
    observer.observe({ entryTypes: ['longtask'] })
    healthTimer = setInterval(() => {
      stopHealth()
      startHealth()
    }, 60_000)
  }
  const onPageHide = () => {
    if (screen?.frame) cancelAnimationFrame(screen.frame)
    screen?.finish('abandoned')
    screen = undefined
    stopHealth()
  }
  const onVisibility = () => {
    if (document.visibilityState === 'hidden') {
      onPageHide()
    } else {
      startHealth()
    }
  }
  document.addEventListener('visibilitychange', onVisibility)
  window.addEventListener('pagehide', onPageHide)
  window.addEventListener('pageshow', startHealth)
  startHealth()
  const cleanup = () => {
    if (monitoringCleanup !== cleanup) return
    onPageHide()
    window.removeEventListener('error', onError)
    window.removeEventListener('unhandledrejection', onRejection)
    document.removeEventListener('visibilitychange', onVisibility)
    window.removeEventListener('pagehide', onPageHide)
    window.removeEventListener('pageshow', startHealth)
    monitoringCleanup = undefined
  }
  monitoringCleanup = cleanup
  return cleanup
}
